import { createContext, useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { resolveApiUrl } from '../utils/resolveApiUrl';

/** Non-secret flag for `if (token)` gates. JWT lives in HttpOnly cookie. */
const SESSION_TOKEN = 'session';

const AuthContext = createContext({
  token: null,
  employeeId: null,
  employeeName: null,
  isAdmin: false,
  appSettings: null,
  featurePermissions: [],
  masterDataPermissions: null,
  login: () => {},
  logout: () => {},
});

export const AuthContextProvider = ({ children }) => {
  const [token, setToken] = useState(null);
  const [employeeId, setEmployeeId] = useState(null);
  const [employeeName, setEmployeeName] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [appSettings, setAppSettings] = useState(null);
  const [featurePermissions, setFeaturePermissions] = useState([]);
  const [masterDataPermissions, setMasterDataPermissions] = useState(null);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  const clearClientSession = useCallback(() => {
    setToken(null);
    setEmployeeId(null);
    setEmployeeName(null);
    setIsAdmin(false);
    setAppSettings(null);
    setFeaturePermissions([]);
    setMasterDataPermissions(null);
    localStorage.removeItem('compassMobileAuth');
  }, []);

  const login = useCallback(
    (
      _token,
      employeeId,
      employeeName,
      expirationDate,
      appSettingsValue = null,
      featurePermissionsValue = [],
      masterDataPermissionsValue = null,
      isAdminValue = false
    ) => {
      setToken(SESSION_TOKEN);
      setEmployeeId(employeeId);
      setEmployeeName(employeeName);
      setIsAdmin(isAdminValue || false);
      setAppSettings(appSettingsValue);
      setFeaturePermissions(featurePermissionsValue || []);
      setMasterDataPermissions(masterDataPermissionsValue);

      const resolvedExpiration =
        expirationDate instanceof Date
          ? expirationDate.toISOString()
          : expirationDate || new Date(Date.now() + 1000 * 60 * 60 * 24).toISOString();

      localStorage.setItem(
        'compassMobileAuth',
        JSON.stringify({
          employeeId,
          employeeName,
          isAdmin: isAdminValue || false,
          appSettings: appSettingsValue,
          featurePermissions: featurePermissionsValue || [],
          masterDataPermissions: masterDataPermissionsValue,
          expirationDate: resolvedExpiration,
        })
      );
    },
    []
  );

  const logout = useCallback(() => {
    axios.post(`${resolveApiUrl()}/users/logout`, null, { withCredentials: true }).catch(() => {});
    clearClientSession();
  }, [clearClientSession]);

  useEffect(() => {
    let isCancelled = false;

    const restoreSession = async () => {
      let storedData = null;
      try {
        storedData = JSON.parse(localStorage.getItem('compassMobileAuth') || 'null');
      } catch {
        localStorage.removeItem('compassMobileAuth');
      }

      if (storedData?.token) {
        const { token: _legacyToken, ...rest } = storedData;
        storedData = rest;
        localStorage.setItem('compassMobileAuth', JSON.stringify(rest));
      }

      const storedExpiration = storedData?.expirationDate
        ? new Date(storedData.expirationDate).getTime()
        : null;
      if (
        storedExpiration != null &&
        !Number.isNaN(storedExpiration) &&
        storedExpiration <= Date.now()
      ) {
        // Stale client mirror only — still validate the HttpOnly cookie (e.g. post-SSO).
        localStorage.removeItem('compassMobileAuth');
        storedData = null;
      }

      try {
        const response = await axios.get(`${resolveApiUrl()}/users/session`, {
          withCredentials: true,
        });
        if (isCancelled) {
          return;
        }
        const data = response.data;
        login(
          SESSION_TOKEN,
          data.employeeId,
          data.userName,
          data.expirationDate,
          data.userSettings || null,
          data.featurePermissions || [],
          data.masterDataPermissions || null,
          data.isAdmin || false
        );
      } catch {
        if (!isCancelled) {
          localStorage.removeItem('compassMobileAuth');
        }
      } finally {
        if (!isCancelled) {
          setIsCheckingAuth(false);
        }
      }
    };

    restoreSession();
    return () => {
      isCancelled = true;
    };
  }, [login]);

  return (
    <AuthContext.Provider
      value={{
        token,
        employeeId,
        employeeName,
        isAdmin,
        appSettings,
        featurePermissions,
        masterDataPermissions,
        isCheckingAuth,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
